import { Collapsible } from "@base-ui/react/collapsible"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"

import AlertBanner from "src/components/AlertBanner"

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const CollapsibleBar = ({
  title,
  badge,
  summary,
  subtitle,
  banner,
  actions,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  accent,
  maxContentHeight,
  className,
  ariaLabel,
  resizable = false,
  minContentHeight = 120,
  maxResizableHeight,
  onHeightChange,
  resizeHandleAriaLabel = "Resize panel content height",
  persistOpenState = true,
  openStateStorageKey,
  alwaysShowSummary = false,
}) => {
  const isControlled = open !== undefined
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen)
  const [resizedMaxHeight, setResizedMaxHeight] = useState(maxContentHeight)
  const contentRef = useRef(null)
  const dragStateRef = useRef(null)
  const resolvedOpenStateStorageKey = useMemo(() => {
    if (!persistOpenState || typeof window === "undefined") return null
    if (openStateStorageKey) return openStateStorageKey

    const raw =
      ariaLabel || (typeof title === "string" ? title : null) || "collapsible"
    const label = raw
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-_]/g, "")
    return `collapsibleBar.open.${label}`
  }, [ariaLabel, openStateStorageKey, persistOpenState, title])

  const isOpen = isControlled ? open : internalIsOpen

  useEffect(() => {
    setResizedMaxHeight(maxContentHeight)
  }, [maxContentHeight])

  useEffect(() => {
    if (isControlled) return

    if (
      !persistOpenState ||
      !resolvedOpenStateStorageKey ||
      typeof window === "undefined"
    ) {
      setInternalIsOpen(defaultOpen)
      return
    }

    const savedState = window.localStorage.getItem(resolvedOpenStateStorageKey)
    if (savedState === "true") {
      setInternalIsOpen(true)
      return
    }
    if (savedState === "false") {
      setInternalIsOpen(false)
      return
    }
    setInternalIsOpen(defaultOpen)
  }, [defaultOpen, isControlled, persistOpenState, resolvedOpenStateStorageKey])

  useEffect(() => {
    if (
      !persistOpenState ||
      !resolvedOpenStateStorageKey ||
      typeof window === "undefined"
    ) {
      return
    }

    window.localStorage.setItem(resolvedOpenStateStorageKey, String(isOpen))
  }, [isOpen, persistOpenState, resolvedOpenStateStorageKey])

  const handleOpenChange = (nextOpen) => {
    if (!isControlled) {
      setInternalIsOpen(nextOpen)
    }

    onOpenChange?.(nextOpen)
  }

  useEffect(() => {
    return () => {
      if (dragStateRef.current?.moveListener) {
        window.removeEventListener(
          "pointermove",
          dragStateRef.current.moveListener
        )
      }
      if (dragStateRef.current?.upListener) {
        window.removeEventListener("pointerup", dragStateRef.current.upListener)
      }
    }
  }, [])

  const appliedHeight = useMemo(() => {
    if (!resizable) return maxContentHeight
    return resizedMaxHeight
  }, [maxContentHeight, resizedMaxHeight, resizable])

  const onResizeStart = (event) => {
    if (!resizable || !isOpen) return
    event.preventDefault()

    const startHeight =
      contentRef.current?.getBoundingClientRect().height || minContentHeight
    const viewportMax =
      typeof window === "undefined"
        ? startHeight
        : Math.floor(window.innerHeight * 0.8)
    const capHeight = maxResizableHeight ?? viewportMax

    const startY = event.clientY

    const moveListener = (moveEvent) => {
      moveEvent.preventDefault()
      const delta = moveEvent.clientY - startY
      const nextHeight = clamp(
        Math.round(startHeight + delta),
        minContentHeight,
        Math.max(minContentHeight, capHeight)
      )
      setResizedMaxHeight(nextHeight)
      if (onHeightChange) onHeightChange(nextHeight)
    }

    const upListener = () => {
      document.body.style.removeProperty("user-select")
      document.body.style.removeProperty("cursor")
      window.removeEventListener("pointermove", moveListener)
      window.removeEventListener("pointerup", upListener)
      dragStateRef.current = null
    }

    document.body.style.userSelect = "none"
    document.body.style.cursor = "ns-resize"

    dragStateRef.current = { moveListener, upListener }
    window.addEventListener("pointermove", moveListener)
    window.addEventListener("pointerup", upListener)
  }

  const root = (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={classNames(
        "overflow-hidden bg-white",
        accent ? "rounded-[7px]" : "rounded-lg border border-gray-300",
        className
      )}
    >
      <div
        className={classNames(
          "bg-white transition-colors hover:bg-blue-50 motion-reduce:transition-none",
          banner
            ? null
            : ["border-b", isOpen ? "border-gray-300" : "border-transparent"]
        )}
      >
        <div className="flex w-full flex-col items-stretch md:flex-row md:items-center">
          <Collapsible.Trigger
            aria-label={ariaLabel}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 bg-transparent px-3 pb-3 pt-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:gap-4 sm:px-5 sm:pb-3.5 sm:pt-3.5"
          >
            {/* Toggle chevron — centered against the header row, including the
                taller title column when a subtitle is present. */}
            <div className="rounded-md flex h-6 w-6 flex-shrink-0 items-center justify-center bg-gray-100 hover:bg-gray-200">
              <i
                className={classNames(
                  "fa-kit fa-angle-down duration-250 text-sm text-gray-600 transition-transform motion-reduce:transition-none",
                  isOpen && "rotate-180"
                )}
              />
            </div>

            {/* Title column — title + badge on top, subtitle below.
                Subtitle stays within this column on desktop so long text
                doesn't run under the actions; on mobile the trigger is
                full-width, so the column naturally spans full-width. */}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="truncate text-sm font-semibold text-gray-600">
                  {title}
                </span>
                {badge}
              </div>
              {subtitle && (
                <div>
                  {typeof subtitle === "function" ? subtitle(isOpen) : subtitle}
                </div>
              )}
            </div>

            {/* Summary — desktop only; invisible-but-layout-preserving when
                open unless alwaysShowSummary. */}
            {summary && (
              <div
                className={classNames("min-w-0 flex-shrink-0", {
                  "hidden md:block": !alwaysShowSummary,
                  invisible: !alwaysShowSummary && isOpen,
                })}
              >
                {typeof summary === "function" ? summary(isOpen) : summary}
              </div>
            )}
          </Collapsible.Trigger>

          {/* Actions — outside trigger to avoid nested <button>. On mobile they
              stack below the trigger row and right-align; on desktop they sit
              inline as a sibling of the trigger. */}
          {actions && (
            <div
              className={classNames(
                "flex flex-shrink-0 items-center justify-end gap-2",
                // narrow viewport (< md): full-width row below the trigger,
                // with breathing room from the title above
                "px-3 pb-3 pt-2",
                // md+: inline beside trigger
                "md:justify-start md:pb-3.5 md:pl-0 md:pr-5 md:pt-3.5"
              )}
            >
              {actions}
            </div>
          )}
        </div>
      </div>

      {banner && (
        <AlertBanner type={banner.type} flush>
          {banner.text}
        </AlertBanner>
      )}

      <Collapsible.Panel
        keepMounted
        className="h-[var(--collapsible-panel-height)] overflow-hidden transition-[height] duration-300 ease-in-out data-[ending-style]:h-0 data-[starting-style]:h-0 motion-reduce:transition-none"
      >
        <div className="px-3 py-3 sm:px-5 sm:py-4">
          <div
            ref={contentRef}
            style={
              appliedHeight
                ? resizable
                  ? {
                      height: appliedHeight,
                      maxHeight: appliedHeight,
                      overflowY: "auto",
                    }
                  : { maxHeight: appliedHeight, overflowY: "auto" }
                : undefined
            }
          >
            {children}
          </div>

          {resizable && isOpen && (
            <div className="mt-2 flex items-center justify-center border-t border-gray-200 pt-4">
              <button
                type="button"
                aria-label={resizeHandleAriaLabel}
                onPointerDown={onResizeStart}
                className="cursor-ns-resize touch-none rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <svg
                  width="20"
                  height="12"
                  viewBox="0 0 20 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 3H16"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M4 6H16"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M4 9H16"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </Collapsible.Panel>
    </Collapsible.Root>
  )

  if (accent) {
    return <div className={classNames("rounded-lg p-0.5", accent)}>{root}</div>
  }

  return root
}

CollapsibleBar.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  badge: PropTypes.node,
  summary: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  subtitle: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  banner: PropTypes.shape({
    text: PropTypes.node.isRequired,
    type: PropTypes.oneOf([
      "info",
      "warning",
      "error",
      "success",
      "neutral",
      "announcement",
      "recommendation",
    ]),
  }),
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  accent: PropTypes.string,
  maxContentHeight: PropTypes.number,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  resizable: PropTypes.bool,
  minContentHeight: PropTypes.number,
  maxResizableHeight: PropTypes.number,
  onHeightChange: PropTypes.func,
  resizeHandleAriaLabel: PropTypes.string,
  persistOpenState: PropTypes.bool,
  openStateStorageKey: PropTypes.string,
  alwaysShowSummary: PropTypes.bool,
}

export default CollapsibleBar
